import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { format } from 'date-fns';
import PropTypes from 'prop-types';

import { config } from '../../api/config';
import { actions } from '../../state';
import { serviceTypes } from '../../utils';
import { isEmpty } from '../../utils';
import { ALL_SERVICES_VALUE } from './discounts.data';
import {
  Header,
  Breadcrumb,
  ButtonSimple,
  Paper,
  PaperHeader,
  TitleSecondary,
} from '../../components';
import Form from './discounts.form';
import { validationErrors } from './discounts.validation';
import styles from './discounts.module.scss';

const BANNER_SIMPLE_TYPE = 652;
const PENDING_DISCOUNT_STATUS = 171;

const normalizeClientGroupsForSubmit = (
  selectedGroups = [],
  availableClientGroups = []
) => {
  if (!selectedGroups.length) {
    return null;
  }

  const availableGroupIds = availableClientGroups.map(group => group.id);

  if (
    availableGroupIds.length &&
    selectedGroups.length === availableGroupIds.length &&
    selectedGroups.every(groupId => availableGroupIds.includes(groupId))
  ) {
    return null;
  }

  return selectedGroups;
};

const normalizeLocationsForSubmit = (
  selectedLocations = [],
  availableLocations = []
) => {
  if (!selectedLocations.length) {
    return null;
  }

  const availableLocationIds = availableLocations.map(location => location.id);

  if (
    availableLocationIds.length &&
    selectedLocations.length === availableLocationIds.length &&
    selectedLocations.every(locationId =>
      availableLocationIds.includes(locationId)
    )
  ) {
    return null;
  }

  return selectedLocations;
};

const areAllServicesSelected = selectedServiceTypes => {
  if (!selectedServiceTypes?.length) {
    return false;
  }

  return serviceTypes.every(serviceType =>
    selectedServiceTypes.includes(serviceType)
  );
};

const normalizeServiceTypesForSubmit = selectedServiceTypes => {
  if (!selectedServiceTypes?.length) {
    return null;
  }

  return areAllServicesSelected(selectedServiceTypes)
    ? null
    : selectedServiceTypes;
};

const EditDiscount = ({
  dispatch,
  history,
  match: { params },
  discounts: { selectedDiscount, loading },
  clientGroups,
  availableLocations,
}) => {
  const [values, setValues] = useState({
    code: '',
    title_lt: '',
    title_en: '',
    title_ru: '',
    description_lt: '',
    description_en: '',
    description_ru: '',
    dateFrom: format(Date.now(), 'yyyy-MM-dd'),
    dateTo: format(Date.now(), 'yyyy-MM-dd'),
    timeFrom: '',
    timeTo: '',
    type: 191,
    usage_limit: 1,
    createDiscountBanner: false,
    bannerPlacements: [],
    announcement: {
      type: BANNER_SIMPLE_TYPE,
      title_lt: '',
      title_en: '',
      title_ru: '',
      description_lt: '',
      description_en: '',
      description_ru: '',
      banner: null,
      banner_id: 0,
      deep_link: '',
    },
  });
  const [locations, setLocations] = useState([]);
  const [services, setServices] = useState([]);
  const [weekdays, setWeekdays] = useState([]);
  const [groups, setGroups] = useState([]);
  const isCodeEditable = selectedDiscount?.status === PENDING_DISCOUNT_STATUS;

  const handleBannerChange = useCallback(banner => {
    setValues(currentValues => ({
      ...currentValues,
      announcement: {
        ...currentValues.announcement,
        banner: banner || null,
        banner_id: banner?.id || 0,
      },
    }));
  }, []);

  const fetchDiscount = useCallback(
    id => {
      dispatch(actions.discounts.getDiscountAction(id));
    },
    [dispatch]
  );

  const loadValues = useCallback(() => {
    if (!isEmpty(selectedDiscount)) {
      const {
        code,
        discount,
        title_lt,
        title_en,
        title_ru,
        description_lt,
        description_en,
        description_ru,
        date_interval,
        time_interval,
        service_discounts,
        service_types,
        client_groups,
        week_days,
        announcement,
        type: discountType,
        usage_limit,
        locations: loadedLocations,
      } = selectedDiscount;

      const normalizedClientGroups = client_groups || [];
      const normalizedLocations = loadedLocations || [];
      const normalizedServiceTypes = (service_types || []).map(Number);
      const normalizedServiceDiscounts = (service_discounts || []).map(
        serviceDiscount => ({
          serviceType: Number(serviceDiscount.service_type),
          discount: Number(serviceDiscount.discount_percent),
        })
      );
      const serviceDiscountValues = normalizedServiceDiscounts.map(
        service => service.discount
      );
      const allServiceDiscountsMatch =
        serviceDiscountValues.length > 0 &&
        new Set(serviceDiscountValues).size === 1;
      const usesLegacyAllServicesDiscount =
        Array.isArray(service_discounts) &&
        service_discounts.length === 0 &&
        normalizedServiceTypes.length === 0 &&
        (discount || discount === 0);
      const servicesAreAll =
        (normalizedServiceDiscounts.length > 0 &&
          areAllServicesSelected(
            normalizedServiceDiscounts.map(service => service.serviceType)
          ) &&
          allServiceDiscountsMatch) ||
        (normalizedServiceTypes.length > 0 &&
          areAllServicesSelected(normalizedServiceTypes)) ||
        usesLegacyAllServicesDiscount;
      const groupIsAll =
        client_groups == null ||
        (normalizedClientGroups.length === 1 &&
          normalizedClientGroups.includes(181));
      const locationsAreAll = loadedLocations == null;
      const hasAnnouncement = Boolean(announcement);
      const bannerType =
        announcement && announcement.type
          ? announcement.type
          : BANNER_SIMPLE_TYPE;
      if (hasAnnouncement) {
        dispatch(
          actions.discounts.setDiscountBannerAction(announcement.banner)
        );
      }

      setValues({
        code: code,
        title_lt: title_lt || description_lt || '',
        title_en: title_en || description_en || '',
        title_ru: title_ru || description_ru || '',
        description_lt: description_lt,
        description_en: description_en,
        description_ru: description_ru,
        dateFrom: format(date_interval[0], 'yyyy-MM-dd'),
        dateTo: format(date_interval[1], 'yyyy-MM-dd'),
        timeFrom: time_interval ? time_interval[0].split(' ')[0] : '',
        timeTo: time_interval ? time_interval[1].split(' ')[0] : '',
        type: discountType || (Number(usage_limit) > 1 ? 192 : 191),
        usage_limit: usage_limit || 1,
        createDiscountBanner: hasAnnouncement,
        bannerPlacements:
          announcement?.placements || announcement?.banner_placements || [],
        announcement: hasAnnouncement
          ? {
              type: bannerType,
              title_lt: announcement ? announcement.title_lt : '',
              title_en: announcement ? announcement.title_en : '',
              title_ru: announcement ? announcement.title_ru : '',
              description_lt: announcement ? announcement.description_lt : '',
              description_en: announcement ? announcement.description_en : '',
              description_ru: announcement ? announcement.description_ru : '',
              banner: announcement ? announcement.banner : null,
              banner_id:
                announcement?.banner?.id || announcement?.banner_id || 0,
              deep_link: announcement ? announcement.deep_link : '',
            }
          : {
              type: BANNER_SIMPLE_TYPE,
              title_lt: '',
              title_en: '',
              title_ru: '',
              description_lt: '',
              description_en: '',
              description_ru: '',
              banner: null,
              banner_id: 0,
              deep_link: '',
            },
      });
      setLocations(
        locationsAreAll
          ? availableLocations.map(location => location.id)
          : normalizedLocations
      );
      setServices(
        (servicesAreAll
          ? [
              {
                serviceType: ALL_SERVICES_VALUE,
                discount:
                  normalizedServiceDiscounts[0]?.discount ?? discount ?? '',
              },
            ]
          : normalizedServiceDiscounts.length
          ? normalizedServiceDiscounts
          : normalizedServiceTypes.length
          ? normalizedServiceTypes.map(serviceType => ({
              serviceType,
              discount: discount ?? '',
            }))
          : discount || discount === 0
          ? [
              {
                serviceType: ALL_SERVICES_VALUE,
                discount,
              },
            ]
          : []) || []
      );
      setWeekdays(week_days || []);
      setGroups(
        groupIsAll
          ? clientGroups.map(group => group.id)
          : normalizedClientGroups
      );
    }
  }, [availableLocations, clientGroups, dispatch, selectedDiscount]);

  const clearDiscounts = useCallback(() => {
    dispatch(actions.discounts.clearDiscountsAction());
  }, [dispatch]);

  useEffect(() => {
    fetchDiscount(params.id);
    return () => clearDiscounts();
  }, [fetchDiscount, clearDiscounts, params.id]);

  useEffect(() => {
    loadValues();
  }, [loadValues]);

  const handleInputChange = ({ target: { name, value } }) => {
    if (name.includes('.')) {
      const splitName = name.split('.');
      setValues({
        ...values,
        announcement: { ...values.announcement, [splitName[1]]: value },
      });
    } else setValues({ ...values, [name]: value });
  };

  const openModal = modalName => {
    const payload = {
      modalName,
      title: 'Deaktyvuoti',
      desc: 'Ar tikrai norite deaktyvuoti šį kodą?',
      onConfirm: () =>
        dispatch(
          actions.discounts.deactivateDiscountAction(params.id, history)
        ),
    };
    dispatch(actions.modals.setModalVisible(payload));
  };

  const handleSubmit = e => {
    e.preventDefault();

    const error = validationErrors(values, services);

    if (error) {
      dispatch(actions.error.setErrorAction({ msg: error }));
    } else {
      const codeToSubmit = values.code;
      const titleLtToSubmit = values.title_lt;
      const titleEnToSubmit = values.title_en;
      const titleRuToSubmit = values.title_ru;
      const descriptionLtToSubmit = values.description_lt;
      const descriptionEnToSubmit = values.description_en;
      const descriptionRuToSubmit = values.description_ru;
      const selectedServices = services.filter(service => {
        const serviceType = service?.serviceType;

        return (
          serviceType !== '' &&
          serviceType !== null &&
          serviceType !== undefined
        );
      });
      const isAllServicesSelected =
        selectedServices.length === 1 &&
        selectedServices[0]?.serviceType === ALL_SERVICES_VALUE;
      const serviceDiscountsToSubmit = isAllServicesSelected
        ? null
        : selectedServices.map(service => ({
            service_type: Number(service.serviceType),
            discount_percent: Number(service.discount),
          }));
      const dateFrom = new Date(values.dateFrom).toISOString();
      const dateTo = new Date(values.dateTo).toISOString();
      const dateIntervalToSubmit = [dateFrom, dateTo];
      const locationsToSubmit = normalizeLocationsForSubmit(
        locations,
        availableLocations
      );
      const serviceTypesToSubmit = isAllServicesSelected
        ? null
        : normalizeServiceTypesForSubmit(
            selectedServices.map(service => Number(service.serviceType))
          );
      const weekdaysToSubmit = weekdays.length ? weekdays : null;
      const groupsToSubmit = normalizeClientGroupsForSubmit(
        groups,
        clientGroups
      );
      const timeToSubmit =
        values.timeFrom === values.timeTo
          ? null
          : [values.timeFrom, values.timeTo];

      const createDiscountBannerToSubmit = values.createDiscountBanner;
      const isMultiUse = Number(values.type) === 192;

      let announcementToSubmit = {};
      if (createDiscountBannerToSubmit) {
        announcementToSubmit.type = values.announcement.type;
        announcementToSubmit.title_lt = titleLtToSubmit;
        announcementToSubmit.title_en = titleEnToSubmit;
        announcementToSubmit.title_ru = titleRuToSubmit;
        announcementToSubmit.description_lt = descriptionLtToSubmit;
        announcementToSubmit.description_en = descriptionEnToSubmit;
        announcementToSubmit.description_ru = descriptionRuToSubmit;
        announcementToSubmit.app_notification = false;
        announcementToSubmit.app_notification_send_at = null;
        announcementToSubmit.deep_link = values.announcement.deep_link;
        announcementToSubmit.placements = values.bannerPlacements;
      }

      const body = {
        code: codeToSubmit,
        description_lt: descriptionLtToSubmit,
        description_en: descriptionEnToSubmit,
        description_ru: descriptionRuToSubmit,
        date_interval: dateIntervalToSubmit,
        ...(isAllServicesSelected
          ? {
              discount: Number(selectedServices[0].discount),
              service_discounts: null,
              service_types: null,
            }
          : {
              service_discounts: serviceDiscountsToSubmit,
              service_types: serviceTypesToSubmit,
            }),
        client_groups: groupsToSubmit,
        locations: locationsToSubmit,
        time_interval: timeToSubmit,
        week_days: weekdaysToSubmit,
        type: Number(values.type),
        usage_limit: isMultiUse ? Number(values.usage_limit) : null,
        createDiscountBanner: createDiscountBannerToSubmit,
        announcement: createDiscountBannerToSubmit
          ? announcementToSubmit
          : null,
      };

      dispatch(actions.discounts.editDiscountAction(params.id, body, history));
    }
  };

  const setCreateDiscountBanner = ({ target: { checked } }) => {
    setValues({
      ...values,
      createDiscountBanner: checked,
    });
  };

  return (
    <section>
      <Header size="sm">
        <Breadcrumb
          title={config.PAGES.discounts}
          subtitle={selectedDiscount.code}
          baseUrl={config.APP_PATHS.discounts}
          variant="pageTitle"
        />
        {!loading && selectedDiscount.status !== 174 && (
          <ButtonSimple type="remove" onClick={() => openModal('confirmModal')}>
            Deaktyvuoti
          </ButtonSimple>
        )}
      </Header>

      <Paper size="sm">
        <PaperHeader>
          <TitleSecondary>Redaguoti nuolaidą</TitleSecondary>
        </PaperHeader>
        <Form
          values={values}
          onChange={handleInputChange}
          onSubmit={handleSubmit}
          onBannerChange={handleBannerChange}
          loading={loading}
          codeDisabled={!loading && !isCodeEditable}
          weekdays={weekdays}
          setWeekdays={setWeekdays}
          services={services}
          setServices={setServices}
          locations={locations}
          setLocations={setLocations}
          groups={groups}
          setGroups={setGroups}
          setCreateDiscountBanner={setCreateDiscountBanner}
        />
      </Paper>
    </section>
  );
};

const mapStateToProps = state => ({
  discounts: state.discounts,
  clientGroups: state.clientGroups.groups,
  availableLocations: state.locations.locations,
});

EditDiscount.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.object.isRequired,
  }).isRequired,
  discounts: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    selectedDiscount: PropTypes.shape({
      id: PropTypes.number,
      code: PropTypes.string,
      discount: PropTypes.number,
      deactivated_at: PropTypes.number,
      service_discounts: PropTypes.arrayOf(
        PropTypes.shape({
          service_type: PropTypes.number,
          discount_percent: PropTypes.number,
        })
      ),
      service_types: PropTypes.arrayOf(PropTypes.number),
      admin_id: PropTypes.number,
      locations: PropTypes.array,
      time_interval: PropTypes.arrayOf(PropTypes.string),
      date_interval: PropTypes.arrayOf(PropTypes.number),
      status: PropTypes.number,
    }).isRequired,
  }).isRequired,
  clientGroups: PropTypes.arrayOf(PropTypes.object).isRequired,
  availableLocations: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default connect(mapStateToProps)(EditDiscount);
